import { useCallback, useEffect, useRef, useState } from "react";
import {
  CONSTANT_SPEED_WPM_LIMITS,
  DEFAULT_CONSTANT_SPEED_WPM,
  DEFAULT_OVERLAY,
  OVERLAY_LIMITS,
  OVERLAY_SLIDERS,
  type OverlayDefaults,
  type OverlaySettingsState,
} from "@cuekards/core/overlay-defaults";
import { SettingNumber, SettingSlider } from "./setting-rows.js";

/** The state before main has answered, so the inputs are never uncontrolled. */
const PENDING: OverlaySettingsState = {
  defaults: DEFAULT_OVERLAY,
  wpm: DEFAULT_CONSTANT_SPEED_WPM,
};

/**
 * The prompter settings screen (ticket 16, FR-61/FR-63/FR-64).
 *
 * These *are* live controls (ADR-0004): every change is stored as the default and
 * put on the window in the same breath, so the screen shows its own effect
 * instead of asking to be applied. The stored side still matters — it is what the
 * next session starts from — but the user does not have to hold two layers in
 * their head to work the sliders.
 *
 * **Width, height and position are not here, and cannot come back.** They were,
 * with an argument: they were never how you placed the window you were looking at,
 * they were what a monitor cuekards had never been opened on would start from — the
 * one placement no amount of dragging could answer in advance. The window answers
 * it itself now: a native resize records the live shape, and `AppWindowController`
 * puts it in the stored defaults — in memory as it happens, on disk when the app
 * quits — so those numbers maintain themselves and a slider would only be a
 * second, worse way to set what the edges have already set.
 * "Save the window as it is" went with them for the
 * same reason: it reconciled a live window with stored numbers that had drifted
 * apart, and nothing drifts any more. The `saveOverlayAsDefaults` channel stays
 * for the web host, whose page has no window to drag.
 *
 * **{@link commit} still sends the whole `OverlayDefaults`**, geometry included
 * and passed through untouched. That is the contract rather than laziness:
 * `setOverlayDefaults` takes a whole object and `sanitiseOverlayDefaults` is
 * total, so a partial payload would silently reset every field it did not mention
 * to the built-in — including the size a resize had just captured.
 *
 * Font size and opacity are sliders: every position they can be dragged to is a
 * value main will accept, so they commit as they move. The one typed field left —
 * the constant-speed rate — commits on blur or Enter instead, because main clamps
 * what it is given and committing per keystroke would rewrite "4" to the minimum
 * rate before the user finished typing "40".
 */
export function OverlayDefaultsPanel(): JSX.Element {
  const [state, setState] = useState<OverlaySettingsState>(PENDING);
  const [draft, setDraft] = useState<OverlayDefaults>(DEFAULT_OVERLAY);
  const [wpmDraft, setWpmDraft] = useState("");
  /** The last defaults this screen sent, so main's echo of them does not
   * overwrite what is in the fields. A broadcast that is *not* our own echo — a
   * reset, or a second view of this screen — is adopted. */
  const sent = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const adopt = (next: OverlaySettingsState): void => {
      setState(next);
      const serialised = JSON.stringify(next.defaults);
      if (serialised !== sent.current) setDraft(next.defaults);
      sent.current = null;
      setWpmDraft(String(next.wpm));
    };
    void window.cuekards.getOverlaySettings().then((s) => {
      if (!cancelled) adopt(s);
    });
    const off = window.cuekards.onOverlaySettingsChanged(adopt);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  const commit = useCallback((next: OverlayDefaults) => {
    setDraft(next);
    sent.current = JSON.stringify(next);
    window.cuekards.setOverlayDefaults(next);
  }, []);

  return (
    <section className="settings-screen">
      <p className="settings-note">
        How the prompter looks, and how fast constant-speed mode reads. The two
        sliders change the window as you drag them, and are what the next session
        starts from. Its size and place are not here — drag the window's edges,
        and where you leave it is where it opens.
      </p>

      {/* Sliders rather than boxes: these are the settings you arrive at by
        * looking at the result rather than by knowing the number, and a slider
        * says what the usable range *is* — which a box clamping silently to
        * 16–96 never did. */}
      <SettingSlider
        label="Font size"
        value={draft.fontSize}
        min={OVERLAY_LIMITS.minFont}
        max={OVERLAY_LIMITS.maxFont}
        step={OVERLAY_SLIDERS.fontStep}
        format={(v) => `${v} px`}
        onChange={(v) => commit({ ...draft, fontSize: v })}
      />
      {/* The whole window, titlebar included, since one-window ticket 03: this is
        * `setOpacity` on an ordinary framed window rather than a transparent
        * page. It is what "unobtrusive" (G2) actually asked for. */}
      <SettingSlider
        label="Opacity"
        value={draft.opacity}
        min={OVERLAY_LIMITS.minOpacity}
        max={OVERLAY_LIMITS.maxOpacity}
        step={0.1}
        format={(v) => `${Math.round(v * 100)}%`}
        onChange={(v) => commit({ ...draft, opacity: v })}
      />

      {/* The constant-speed rate (FR-35/FR-61). It belongs on this screen and not
        * with the scroll modes: it is a number you set once and then forget, not
        * something you reach for mid-take. */}
      <SettingNumber
        label="Constant-speed rate"
        unit="wpm"
        value={wpmDraft}
        min={CONSTANT_SPEED_WPM_LIMITS.min}
        max={CONSTANT_SPEED_WPM_LIMITS.max}
        onCommit={(raw) => {
          if (raw.trim() === "") {
            setWpmDraft(String(state.wpm));
            return;
          }
          window.cuekards.setConstantSpeedWpm(Number(raw));
        }}
      />

      <div className="settings-actions">
        {/* FR-64, narrowed to the three things this screen sets. It restores the
          * built-in font size, opacity and rate and puts them on the window; it
          * does not move or resize it. Never greyed, even when the built-ins are
          * already what is stored: reset also *applies* them, so it is the way
          * back from a font size dialled somewhere unreadable — the one moment a
          * greyed button here would be worst. */}
        <button
          type="button"
          className="settings-reset"
          onClick={() => window.cuekards.resetOverlaySettings()}
          title="Restore the built-in font size, opacity and rate, and apply them"
        >
          Reset to defaults
        </button>
      </div>
    </section>
  );
}
